import React, { useState } from 'react';
import { Sparkles, X, Check, CreditCard } from 'lucide-react';
import { Button, toast } from './V2Components';
import SubscriptionService from '../../services/SubscriptionService';
import { appUrl } from '../../utils/appUrl';
import { billingErrorMessage } from '../../utils/billingErrors';

// Premium tier highlights (mirrors utils/subscription_config.py paid limits).
const PREMIUM_BENEFITS = [
  '5 houses, 50 rooms per house',
  '1,000 inventory items',
  '20 GB of storage',
  '25 document scans every month',
];

// error_code on the 409 from POST /subscriptions/checkout when this account
// already holds a live paid subscription (AlreadySubscribedError.error_code in
// services/stripe_service.py). A second Checkout would double-bill, so the
// server refuses and the modal offers the billing portal instead.
export const ALREADY_SUBSCRIBED_CODE = 'ALREADY_SUBSCRIBED';

/**
 * Upgrade paywall modal. Shown app-wide by PaywallProvider when a write is
 * blocked (403 LIMIT_EXCEEDED), or opened manually via useUpgrade().
 *
 * Upgrading creates a hosted Stripe Checkout Session server-side and redirects
 * the browser to it — no Stripe.js needed.
 *
 * @param {boolean} isOpen
 * @param {Object|null} details - { category, current_usage, limit, message } when limit-triggered
 * @param {Function} onClose
 */
export default function UpgradeModal({ isOpen, details, onClose }) {
  const [loading, setLoading] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setAlreadySubscribed(false);
    onClose();
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const url = await SubscriptionService.createCheckoutSession({
        successUrl: appUrl('/subscription/success'),
        cancelUrl: appUrl('/subscription/cancel'),
      });
      // Hosted Checkout — leave the SPA for Stripe's page.
      window.location.href = url;
    } catch (err) {
      if (err?.errorCode === ALREADY_SUBSCRIBED_CODE) {
        // The paywall fired on stale limits, or Upgrade was clicked in a second
        // tab: this account already pays for Premium. Swap the CTA for the
        // billing portal rather than toasting an error the user can't act on.
        setAlreadySubscribed(true);
        setLoading(false);
        return;
      }
      // BILLING_UNAVAILABLE (503) means Stripe itself is misconfigured or down —
      // show billing-specific copy rather than "try again" (see billingErrors.js).
      toast.error(billingErrorMessage(err, 'Could not start checkout. Please try again.'));
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setLoading(true);
    try {
      const url = await SubscriptionService.openBillingPortal({ returnUrl: appUrl('/subscription') });
      window.location.href = url;
    } catch (err) {
      toast.error(billingErrorMessage(err, 'Could not open the billing portal. Please try again.'));
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Upgrade to Premium"
    >
      <div className="bg-[var(--bg-card)] w-full max-w-md rounded-2xl shadow-xl p-6 theme-transition">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[var(--primary)]" />
            <h2 className="text-lg font-semibold text-[var(--text-main)]">Upgrade to Premium</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="text-[var(--text-secondary)] hover:text-[var(--text-main)] theme-transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {alreadySubscribed ? (
          <p className="mb-4 text-sm text-[var(--text-secondary)]" role="status">
            This account already has an active Premium subscription, so we didn’t start another checkout.
            You can review, renew, or change it from the billing portal.
          </p>
        ) : details?.message ? (
          <p className="mb-4 text-sm text-[var(--text-secondary)]">{details.message}</p>
        ) : (
          <p className="mb-4 text-sm text-[var(--text-secondary)]">
            Unlock more space for your home inventory.
          </p>
        )}

        <ul className="mb-5 space-y-2">
          {PREMIUM_BENEFITS.map((benefit) => (
            <li key={benefit} className="flex items-center gap-2 text-sm text-[var(--text-main)]">
              <Check className="w-4 h-4 text-[var(--primary)] shrink-0" />
              {benefit}
            </li>
          ))}
        </ul>

        <div className="flex gap-2">
          {alreadySubscribed ? (
            <>
              <Button variant="ghost" onClick={handleClose} fullWidth disabled={loading}>
                Close
              </Button>
              <Button variant="primary" leadingIcon={CreditCard} onClick={handleManageBilling} loading={loading} fullWidth>
                Manage billing
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={handleClose} fullWidth disabled={loading}>
                Maybe later
              </Button>
              <Button variant="primary" onClick={handleUpgrade} loading={loading} fullWidth>
                Upgrade
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
